import React, { ReactNode } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

const ModernDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    borderRadius: 20,
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflow: "visible",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background:
        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
    },
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    backdropFilter: "blur(8px)",
  },
}));

const ModernDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(3, 4, 2, 4),
  background: "transparent",
  borderBottom: "none",
  position: "relative",
  "& .MuiTypography-root": {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.015em",
    lineHeight: 1.3,
  },
}));

const ModernDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  "&.MuiDialogContent-dividers": {
    borderTop: "none",
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
  },
}));

const ModernDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 4, 3, 4),
  gap: theme.spacing(1.5),
  "& .MuiButton-root": {
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
    padding: theme.spacing(1, 2.5),
    minHeight: 44,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&.MuiButton-contained": {
      background: "linear-gradient(135deg, #1976d2, #1565c0)",
      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #1565c0, #0d47a1)",
        boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
        transform: "translateY(-1px)",
      },
    },
    "&.MuiButton-outlined": {
      borderColor: "rgba(0, 0, 0, 0.12)",
      color: "#6b7280",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        borderColor: "rgba(0, 0, 0, 0.2)",
      },
    },
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
  top: theme.spacing(2),
  color: "#6b7280",
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  width: 40,
  height: 40,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    color: "#ef4444",
    borderColor: "rgba(239, 68, 68, 0.2)",
    transform: "scale(1.05)",
  },
}));

const Transition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>,
) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return <Slide direction="up" ref={ref} {...props} />;
  }

  return <Fade ref={ref} {...props} />;
});

interface ModernModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  disableEscapeKeyDown?: boolean;
  disableBackdropClick?: boolean;
  disableCloseButton?: boolean;
}

export default function ModernModal({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "sm",
  fullWidth = true,
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  disableCloseButton = false,
}: ModernModalProps) {
  const handleClose = (event: any, reason: string) => {
    if (disableBackdropClick && reason === "backdropClick") {
      return;
    }
    if (disableEscapeKeyDown && reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  return (
    <ModernDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      TransitionComponent={Transition}
      aria-labelledby="modern-modal-title"
      aria-describedby="modern-modal-description"
      disableRestoreFocus={false}
      keepMounted={false}
      disableEnforceFocus={false}
      disableAutoFocus={false}
    >
      <ModernDialogTitle id="modern-modal-title">
        <Typography component="span" variant="h5">
          {title}
        </Typography>
        <CloseButton
          aria-label="close"
          onClick={onClose}
          disabled={disableCloseButton}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </CloseButton>
      </ModernDialogTitle>

      <ModernDialogContent dividers id="modern-modal-description">
        {children}
      </ModernDialogContent>

      {actions && <ModernDialogActions>{actions}</ModernDialogActions>}
    </ModernDialog>
  );
}
